"use client"

import { CaretDown } from "@phosphor-icons/react"
import { SelectField } from "@tokenterminal/core/ui/input/SelectField"
import { useState } from "react"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

// Mock API response data matching the screenshot
const mockApiResponse = {
  data: [
    {
      timestamp: "2024-10-21T00:00:00.000Z",
      project_name: "Base",
      project_id: "base",
      afpu: 0.06455671653448596,
      arpu: 0.06455671653448596,
      fees: 107315.92228781925,
      revenue: 107315.92228781925,
      earnings: 106651.32039611327,
      expenses: 664.601891705989,
      user_dau: 1662351,
    },
  ],
}

// JSON syntax highlighting colors
const colors = {
  string: "#E5C07B", // yellow/gold for string values
  number: "#C678DD", // purple/violet for numbers
  key: "inherit", // default text color for keys
  punctuation: "inherit", // default for brackets, colons, commas
}

interface JsonLineProps {
  lineNumber: number
  content: React.ReactNode
}

function JsonLine({ lineNumber, content }: JsonLineProps) {
  return (
    <div className="flex">
      <span className="w-8 text-right pr-4 text-fg-secondary select-none shrink-0">
        {lineNumber}
      </span>
      <span className="flex-1">{content}</span>
    </div>
  )
}

function JsonKey({ children }: { children: string }) {
  return <span className="text-fg-default">&quot;{children}&quot;</span>
}

function JsonString({ children }: { children: string }) {
  return <span style={{ color: colors.string }}>&quot;{children}&quot;</span>
}

function JsonNumber({ children }: { children: number }) {
  return <span style={{ color: colors.number }}>{children}</span>
}

function Indent({ level }: { level: number }) {
  return <span>{" ".repeat(level * 2)}</span>
}

function APIResponseViewer() {
  const response = mockApiResponse.data[0]
  const [httpMethod, setHttpMethod] = useState("GET")
  const [apiUrl, setApiUrl] = useState("api.tokenterminal.com/v2/project/base/metrics")

  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl bg-bg-default"
      style={FADE_MASK_STYLE}
    >
      {/* Content area */}
      <div className="p-6">
        {/* HTTP Method and URL Bar */}
        <div className="flex items-center gap-2 mb-4">
          <SelectField
            aria-label="HTTP Method"
            selectedKey={httpMethod}
            onSelectionChange={(value) => setHttpMethod(value as string)}
            buttonProps={{
              size: "small",
              className: "!w-24 shrink-0",
            }}
            popoverProps={{
              placement: "bottom start",
            }}
          >
            <SelectField.Option id="GET">GET</SelectField.Option>
            <SelectField.Option id="POST">POST</SelectField.Option>
            <SelectField.Option id="PUT">PUT</SelectField.Option>
            <SelectField.Option id="DELETE">DELETE</SelectField.Option>
          </SelectField>
          <input
            type="text"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            className="flex-1 h-tt-small px-3 text-sm font-mono text-fg-default bg-bg-default border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
          />
        </div>

        {/* Status Bar */}
        <div className="flex items-center gap-2 font-mono text-sm mb-4">
          <span className="text-green-600 dark:text-[#1AFFAB]">200 OK</span>
          <span className="text-fg-secondary">•</span>
          <span className="text-fg-secondary">309 ms</span>
          <span className="text-fg-secondary">•</span>
          <span className="text-fg-secondary">535.2 KB</span>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-6 mb-6 text-sm">
          <button className="flex items-center gap-1 text-fg-default">
            Pretty
            <CaretDown className="w-3 h-3" />
          </button>
          <span className="text-fg-secondary">
            Headers <span className="opacity-60">14</span>
          </span>
          <span className="text-fg-secondary">Info</span>
        </div>

        {/* JSON Code Viewer */}
        <div className="font-mono text-sm leading-relaxed whitespace-pre">
          <JsonLine lineNumber={1} content={"{"} />
          <JsonLine
            lineNumber={2}
            content={
              <>
                <Indent level={1} />
                <JsonKey>data</JsonKey>: [
              </>
            }
          />
          <JsonLine
            lineNumber={3}
            content={
              <>
                <Indent level={2} />
                {"{"}
              </>
            }
          />
          <JsonLine
            lineNumber={4}
            content={
              <>
                <Indent level={3} />
                <JsonKey>timestamp</JsonKey>: <JsonString>{response.timestamp}</JsonString>,
              </>
            }
          />
          <JsonLine
            lineNumber={5}
            content={
              <>
                <Indent level={3} />
                <JsonKey>project_name</JsonKey>: <JsonString>{response.project_name}</JsonString>,
              </>
            }
          />
          <JsonLine
            lineNumber={6}
            content={
              <>
                <Indent level={3} />
                <JsonKey>project_id</JsonKey>: <JsonString>{response.project_id}</JsonString>,
              </>
            }
          />
          <JsonLine
            lineNumber={7}
            content={
              <>
                <Indent level={3} />
                <JsonKey>afpu</JsonKey>: <JsonNumber>{response.afpu}</JsonNumber>,
              </>
            }
          />
          <JsonLine
            lineNumber={8}
            content={
              <>
                <Indent level={3} />
                <JsonKey>arpu</JsonKey>: <JsonNumber>{response.arpu}</JsonNumber>,
              </>
            }
          />
          <JsonLine
            lineNumber={9}
            content={
              <>
                <Indent level={3} />
                <JsonKey>fees</JsonKey>: <JsonNumber>{response.fees}</JsonNumber>,
              </>
            }
          />
          <JsonLine
            lineNumber={10}
            content={
              <>
                <Indent level={3} />
                <JsonKey>revenue</JsonKey>: <JsonNumber>{response.revenue}</JsonNumber>,
              </>
            }
          />
          <JsonLine
            lineNumber={11}
            content={
              <>
                <Indent level={3} />
                <JsonKey>earnings</JsonKey>: <JsonNumber>{response.earnings}</JsonNumber>,
              </>
            }
          />
          <JsonLine
            lineNumber={12}
            content={
              <>
                <Indent level={3} />
                <JsonKey>expenses</JsonKey>: <JsonNumber>{response.expenses}</JsonNumber>,
              </>
            }
          />
          <JsonLine
            lineNumber={13}
            content={
              <>
                <Indent level={3} />
                <JsonKey>user_dau</JsonKey>: <JsonNumber>{response.user_dau}</JsonNumber>,
              </>
            }
          />
          <JsonLine
            lineNumber={14}
            content={
              <>
                <Indent level={2} />
                {"}"}
              </>
            }
          />
          <JsonLine
            lineNumber={15}
            content={
              <>
                <Indent level={1} />]
              </>
            }
          />
          <JsonLine lineNumber={16} content={"}"} />
        </div>
      </div>
    </div>
  )
}

export { APIResponseViewer }
